import React from "react";
import { PageHero, Section, OBJECTIVES, FEATURES, STAFF } from "./_shared";
import { Target, ShieldCheck, Award, Calendar, Users } from "lucide-react";

export default function About() {
  return (
    <div data-testid="mitcidco-about">
      <PageHero
        eyebrow="About the Institute"
        title="Marathwada Private Industrial Training Institute · CIDCO"
        sub="A Gramaudyogik Shikshan Mandal initiative — established in 2017 to serve the vocational training needs of youth in Chhatrapati Sambhajinagar and neighbouring districts."
      />

      <Section>
        <div className="grid md:grid-cols-[1fr_auto] gap-10 items-start">
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-800 leading-relaxed">
              Gramaudyogik Shikshan Mandal, Chhatrapati Sambhajinagar recognised the deep-rooted need for quality vocational training among the region&apos;s rural, aspiring youth. This institute was established in 2017 as a natural extension of the Mandal&apos;s educational mission — bringing government-parallel ITI training to CIDCO N-4, right on the MIT Campus.
            </p>
            <p className="mt-4 text-slate-800 leading-relaxed">
              Over 600 trainees have completed their programmes here and are today employed in leading manufacturing companies, government establishments and self-employment ventures. The institute&apos;s curriculum, examination system and certification are identical to those of Government ITIs.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FactCard icon={<Calendar />} label="Established" value="2017" />
              <FactCard icon={<Award />} label="DVET Code" value="272515101501" />
              <FactCard icon={<ShieldCheck />} label="NCVT MIS Code" value="PU27000880" />
              <FactCard icon={<Target />} label="Affiliation" value="DGT · NCVT · DVET" />
            </div>
          </div>

          <aside className="bg-white border-l-4 border-[#D4A017] p-6 md:min-w-[300px] shadow-sm">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#B45309] font-bold">Address</div>
            <div className="mt-2 text-sm text-slate-800 leading-relaxed">
              Plot No. 37, MIT Campus,<br />
              CIDCO N-4,<br />
              Chhatrapati Sambhajinagar,<br />
              Maharashtra — 431003
            </div>
            <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-slate-500">Managed by</div>
            <div className="mt-1 text-sm font-semibold text-[#7C2D12]">Gramaudyogik Shikshan Mandal,<br />Chhatrapati Sambhajinagar</div>
            <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-slate-500">Government Recognition</div>
            <div className="mt-1 text-[11px] text-slate-700 leading-relaxed">
              GR No. ITI-2012/Pra.Kra.14/Vyashi-3 (Bhag-1), dated 12 October 2023 — Higher &amp; Technical Education Dept., Govt. of Maharashtra.
            </div>
          </aside>
        </div>
      </Section>

      {/* Objectives */}
      <div className="bg-[#7C2D12] text-white">
        <Section className="!py-16">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4A017] font-bold">Institute Objectives · संस्थेचा मुख्य उद्देश</div>
          <h2 className="font-serif text-3xl mt-2">Why we exist</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {OBJECTIVES.map((o, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A017] font-bold">Objective {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 text-white/90 leading-relaxed">{o}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Features */}
      <Section>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">Institute Features · संस्थेची वैशिष्ट्ये</div>
        <h2 className="font-serif text-3xl mt-2 text-[#7C2D12]">What sets us apart</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-amber-100 p-5">
              <div className="w-8 h-8 shrink-0 bg-[#D4A017]/15 text-[#B45309] font-bold flex items-center justify-center text-sm font-mono">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-sm text-slate-800 leading-relaxed pt-1">{f}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Staff */}
      <div className="bg-white border-t border-amber-100">
        <Section>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">Faculty &amp; Staff · कर्मचारी माहिती</div>
          <h2 className="font-serif text-3xl mt-2 text-[#7C2D12]">Our people</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {STAFF.map((s, i) => (
              <div key={i} className="bg-[#FDF6E3] border border-amber-100 p-5 flex items-start gap-4" data-testid={`mitcidco-staff-${i}`}>
                <div className="w-12 h-12 shrink-0 rounded-full bg-[#7C2D12] text-white flex items-center justify-center">
                  <Users size={20} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#7C2D12] leading-tight">{s.name || "—"}</div>
                  <div className="text-[11px] tracking-[0.15em] uppercase text-[#B45309] font-bold mt-0.5">{s.designation}</div>
                  {s.qualification && <div className="mt-2 text-xs text-slate-700 leading-relaxed"><span className="font-semibold text-[#5B1A0A]">Qualification: </span>{s.qualification}</div>}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                    {s.dob && <span><span className="font-semibold text-[#5B1A0A]">DOB:</span> {s.dob}</span>}
                    {s.phone && <span><span className="font-semibold text-[#5B1A0A]">Phone:</span> {s.phone}</span>}
                    {s.email && <a href={`mailto:${s.email}`} className="text-[#B45309] hover:underline break-all">{s.email}</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function FactCard({ icon, label, value }) {
  return (
    <div className="bg-white border border-amber-100 p-4 flex items-center gap-3">
      <div className="w-10 h-10 flex items-center justify-center bg-[#D4A017]/15 text-[#B45309]">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</div>
        <div className="text-sm font-bold text-[#7C2D12] truncate">{value}</div>
      </div>
    </div>
  );
}
